<script lang="ts">
  // A stack's members as a row of cells in their review states: a toolbar
  // with one tab stop (arrow keys, Home and End move along it). Hovering or
  // focusing a cell names its pull request beside the map; choosing one
  // goes to its row.
  import { controlCommand } from '../keys/control';
  import { stackMapKeys } from '../keys/controls';
  import type { MapCell } from '../logic/stackState';

  interface Props {
    cells: MapCell[];
    /** A cell's name, for its label and the caption. */
    label: (cell: MapCell) => string;
    /** The cell of the stack's start, marked. */
    start: number | null;
    /** Names the stack, for the toolbar's label. */
    stackLabel: string;
    /** Whether the toolbar takes the Tab key; the dashboard gives it to the stack the keyboard cursor is in. */
    tabbable?: boolean;
    onpick: (cell: MapCell) => void;
  }
  let { cells, label, start, stackLabel, tabbable = true, onpick }: Props = $props();

  let focus = $state(0);
  let shown: MapCell | null = $state(null);
  let buttons: HTMLButtonElement[] = $state([]);
  const current = $derived(Math.min(focus, cells.length - 1));

  /** Arrows, Home and End move along the map (see `stackMapKeys`). */
  function key(e: KeyboardEvent, i: number) {
    const c = controlCommand(stackMapKeys, e);
    if (!c) return;
    const to = c === 'next' ? Math.min(i + 1, cells.length - 1) : c === 'prev' ? Math.max(i - 1, 0) : c === 'first' ? 0 : cells.length - 1;
    e.preventDefault();
    focus = to;
    buttons[to]?.focus();
  }
</script>

<span class="stack-map">
  <span class="stack-map-cells" role="toolbar" aria-label="Go to a pull request in {stackLabel}" aria-orientation="horizontal">
    {#each cells as c, i (c.index)}
      <button
        bind:this={buttons[i]}
        class="stack-map-cell {c.state}"
        class:start={c.index === start}
        tabindex={tabbable && i === current ? 0 : -1}
        aria-label={label(c)}
        title={label(c)}
        onclick={() => {
          focus = i;
          onpick(c);
        }}
        onkeydown={(e) => key(e, i)}
        onfocus={() => {
          focus = i;
          shown = c;
        }}
        onblur={() => (shown = null)}
        onpointerenter={() => (shown = c)}
        onpointerleave={() => (shown = null)}
        >{c.state === 'approved' ? '✓' : c.state === 'changes' ? '✕' : c.state === 'yours' ? '●' : ''}</button
      >
    {/each}
  </span>
  <span class="stack-map-caption" aria-hidden="true">{shown ? label(shown) : ''}</span>
</span>
